/**
 * Press and hold (long press) for touch, with desktop right-click mapped to the same action.
 *
 * Fires after `delay` (380ms, a little quicker than the iOS context-menu feel) if the finger stays within
 * `tolerance` (8px). Moving further, any scroll, pointercancel (the browser took the touch for a scroll) or a
 * second finger cancels. A right-click (`contextmenu`) fires immediately; the `contextmenu` Android sends after
 * a touch long-press is swallowed so the action doesn't run twice. The click that follows a fired hold is
 * suppressed, so a held row doesn't also open.
 *
 *   const hold = usePressAndHold((e) => openMenu(e), { haptic: true })
 *   <div v-bind="hold.handlers" :class="{ 'is-held': hold.pressing.value }">
 *
 * Put `-webkit-touch-callout: none; user-select: none` on held elements (native does this for buttons and
 * links already) so iOS doesn't start a text selection underneath. As a backstop, a touch press blocks
 * `selectstart` until the finger lifts and clears any selection left when it does: iOS's own long-press fires
 * after ours, and with an unselectable target it can select the whole surrounding block instead.
 */
import { getCurrentScope, onScopeDispose, readonly, ref, toValue, type MaybeRefOrGetter, type Ref } from 'vue'
import { haptic as fireHaptic, type HapticIntent } from '@/native/haptics'

export const HOLD_DELAY_MS = 380
export const HOLD_TOLERANCE_PX = 8
/** A `contextmenu` this soon after a fired hold is the platform's own long-press echo. */
const ECHO_MS = 1000
/** The click after a fired hold is suppressed if it arrives within this window after release. */
const CLICK_SUPPRESS_MS = 600

export interface HoldEvent {
    x: number
    y: number
    target: EventTarget | null
    pointerType: string
    /** 'press' for a long press, 'contextmenu' for a right-click. */
    source: 'press' | 'contextmenu'
}

export interface PressAndHoldOptions {
    delay?: number
    tolerance?: number
    /** Haptic when the hold fires: `true` = 'commit'. Default off. */
    haptic?: boolean | HapticIntent
    /** Map desktop right-click to the hold. Default true. */
    contextMenu?: boolean
    disabled?: MaybeRefOrGetter<boolean>
    /**
     * Arm only for presses on targets this accepts (handlers bound on a container with a menu on some rows):
     * anywhere else a press is a plain tap, the click isn't swallowed and the native context menu is left alone.
     */
    accept?: (target: EventTarget | null) => boolean
}

export interface PressAndHoldHandlers {
    onPointerdown: (e: PointerEvent) => void
    onPointermove: (e: PointerEvent) => void
    onPointerup: (e: PointerEvent) => void
    onPointercancel: (e: PointerEvent) => void
    onPointerleave: (e: PointerEvent) => void
    onContextmenu: (e: MouseEvent) => void
    onClickCapture: (e: MouseEvent) => void
}

export interface PressAndHold {
    /** Spread on the element: `v-bind="hold.handlers"`. */
    handlers: PressAndHoldHandlers
    /** A press is in progress (finger down, hold not fired yet): use it for a "charging" style. */
    pressing: Readonly<Ref<boolean>>
    cancel(): void
}

export function usePressAndHold(onHold: (e: HoldEvent) => void, opts: PressAndHoldOptions = {}): PressAndHold {
    const delay = opts.delay ?? HOLD_DELAY_MS
    const tolerance = opts.tolerance ?? HOLD_TOLERANCE_PX
    const intent: HapticIntent | null = opts.haptic === true ? 'commit' : opts.haptic || null
    const pressing = ref(false)

    let timer: ReturnType<typeof setTimeout> | null = null
    let suppressTimer: ReturnType<typeof setTimeout> | null = null
    let start = { x: 0, y: 0, id: -1, type: 'touch', target: null as EventTarget | null }
    let suppressClick = false
    let lastFireAt = -Infinity
    let scrollListening = false
    let selectBlocking = false
    let releaseTimer: ReturnType<typeof setTimeout> | null = null

    const onScroll = () => cancel()
    function listenScroll(on: boolean) {
        if (on === scrollListening || typeof window === 'undefined') return
        scrollListening = on
        if (on) window.addEventListener('scroll', onScroll, { capture: true, passive: true })
        else window.removeEventListener('scroll', onScroll, { capture: true })
    }

    const onSelectStart = (e: Event) => e.preventDefault()
    function blockSelect(on: boolean) {
        if (on === selectBlocking || typeof document === 'undefined') return
        selectBlocking = on
        if (on) document.addEventListener('selectstart', onSelectStart, true)
        else document.removeEventListener('selectstart', onSelectStart, true)
    }
    function clearSelection() {
        const sel = typeof window !== 'undefined' ? window.getSelection?.() : null
        if (sel && sel.type === 'Range') sel.removeAllRanges()
    }

    function clearTimer() {
        if (timer) clearTimeout(timer)
        timer = null
    }

    function cancel() {
        clearTimer()
        pressing.value = false
        listenScroll(false)
    }

    function fire(e: HoldEvent) {
        clearTimer()
        pressing.value = false
        listenScroll(false)
        lastFireAt = Date.now()
        if (intent) fireHaptic(intent)
        onHold(e)
    }

    const handlers: PressAndHoldHandlers = {
        onPointerdown(e) {
            if (toValue(opts.disabled)) return
            if (!e.isPrimary) return cancel() // a second finger: pinch or two-finger scroll
            if (e.pointerType === 'mouse' && e.button !== 0) return // right-click comes through contextmenu
            cancel()
            if (opts.accept && !opts.accept(e.target)) return
            suppressClick = false
            start = { x: e.clientX, y: e.clientY, id: e.pointerId, type: e.pointerType, target: e.target }
            pressing.value = true
            listenScroll(true)
            if (e.pointerType !== 'mouse') {
                if (releaseTimer) clearTimeout(releaseTimer)
                releaseTimer = null
                blockSelect(true)
            }
            timer = setTimeout(() => {
                suppressClick = true
                fire({ x: start.x, y: start.y, target: start.target, pointerType: start.type, source: 'press' })
            }, delay)
        },
        onPointermove(e) {
            if (!pressing.value || e.pointerId !== start.id) return
            if (Math.hypot(e.clientX - start.x, e.clientY - start.y) > tolerance) cancel()
        },
        onPointerup(e) {
            if (e.pointerId === start.id) {
                cancel()
                if (selectBlocking) clearSelection()
                blockSelect(false)
            }
            if (suppressClick) {
                if (suppressTimer) clearTimeout(suppressTimer)
                suppressTimer = setTimeout(() => (suppressClick = false), CLICK_SUPPRESS_MS)
            }
        },
        onPointercancel(e) {
            if (e.pointerId !== start.id) return
            cancel()
            if (Date.now() - lastFireAt >= ECHO_MS) return blockSelect(false)
            // After a fired hold this is iOS taking the touch for its own long press: the selection it starts
            // comes next, so keep blocking a moment longer and clear whatever got through.
            if (releaseTimer) clearTimeout(releaseTimer)
            releaseTimer = setTimeout(() => {
                releaseTimer = null
                clearSelection()
                blockSelect(false)
            }, ECHO_MS)
        },
        onPointerleave(e) {
            if (e.pointerType === 'mouse' && e.pointerId === start.id) cancel()
        },
        onContextmenu(e) {
            if (opts.contextMenu === false || toValue(opts.disabled)) return
            if (opts.accept && !opts.accept(e.target)) return
            e.preventDefault()
            // The platform's own long-press menu after our hold (Android), or during a touch press: swallow it.
            if (pressing.value || Date.now() - lastFireAt < ECHO_MS) return
            fire({ x: e.clientX, y: e.clientY, target: e.target, pointerType: 'mouse', source: 'contextmenu' })
        },
        onClickCapture(e) {
            if (!suppressClick) return
            suppressClick = false
            e.preventDefault()
            e.stopPropagation()
        },
    }

    if (getCurrentScope()) {
        onScopeDispose(() => {
            cancel()
            blockSelect(false)
            if (releaseTimer) clearTimeout(releaseTimer)
            if (suppressTimer) clearTimeout(suppressTimer)
        })
    }

    return { handlers, pressing: readonly(pressing), cancel }
}
